import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addEditGuideTourHealthDialysisReport,
  addEditGuideTourHealthMedicineReport,
  guideTourGeneralCustomerList,
} from '../api';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useAuth } from '../hooks/useAuth';
import { IGeneralCustomerList, IGuideTourMedicalReport } from '../utils/types';

interface AddEditGuidanceTourHealthMedicineAndDialysisReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (program?: IGuideTourMedicalReport) => void;
  guideSalesIndividualId: string;
  isDialysis?: boolean;
}

export function AddEditGuidanceTourHealthMedicineAndDialysisReportDialog({
  open,
  onClose,
  onSave,
  guideSalesIndividualId,
  isDialysis,
}: AddEditGuidanceTourHealthMedicineAndDialysisReportDialogProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [generalCustomerList, setGeneralCustomerList] = useState<
    IGeneralCustomerList[]
  >([]);
  const [data, setData] = useState<IGuideTourMedicalReport>();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileName, setFileName] = useState<string | null>(null);
  const showSnackbar = useAlertSnackbar();

  useEffect(() => {
    fetchGuideTourGeneralCustomerList(guideSalesIndividualId);
  }, [guideSalesIndividualId]);

  const fetchGuideTourGeneralCustomerList = async (id: string) => {
    try {
      const res = await guideTourGeneralCustomerList(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setGeneralCustomerList(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAddEditMedicineReport = async (isDialysis?: boolean) => {
    try {
      setLoading(true);

      const res = await (isDialysis
        ? addEditGuideTourHealthDialysisReport(
            data?.id,
            token,
            guideSalesIndividualId,
            data?.salesIndividualId,
            file,
          )
        : addEditGuideTourHealthMedicineReport(
            data?.id,
            token,
            guideSalesIndividualId,
            data?.salesIndividualId,
            file,
          ));

      if (res.status === 413) {
        const tooLargeMsg = t(
          'guidanceTourHealth.medicineReportDialog.tooLargeError',
        );
        setError(tooLargeMsg);
        showSnackbar(tooLargeMsg, 'error');
        return;
      }

      if (res.status !== 200 && res.status !== 201) {
        let errorMessage = `Request failed with status ${res.status}`;

        try {
          const errorResponse = await res.json();
          errorMessage = errorResponse.detail || errorMessage;
        } catch (jsonError) {
          console.error('Error parsing JSON response:', jsonError);
        }

        throw new Error(errorMessage);
      }
    } catch (e: any) {
      const errorMessage = e.message || 'Beklenmeyen bir hata oluştu.';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleSave = async () => {
    await fetchAddEditMedicineReport(isDialysis);
    onSave(data);
    onClose();
    handleClose();
  };

  const resetForm = () => {
    setData(undefined);
    setFile(undefined);
    setFileName(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t(
          isDialysis
            ? t('guidanceTourHealth.dialysisReportDialog.addDialysisReport')
            : t('guidanceTourHealth.medicineReportDialog.addReport'),
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormControl fullWidth>
            <Autocomplete
              options={generalCustomerList}
              getOptionLabel={(option) => option.fullName || ''}
              value={generalCustomerList.find(
                (customer) =>
                  customer.salesIndividualId === data?.salesIndividualId,
              )}
              onChange={(e, newValue) => {
                setData((prev) => ({
                  ...prev,
                  salesIndividualId: newValue?.salesIndividualId || 0,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('guidanceTourHealth.disabilityDialog.customerName')}
                  size="small"
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth>
            <input
              type="file"
              id="file-input"
              accept="*/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input">
              <Button variant="contained" component="span">
                {t('guidanceTourHealth.medicineReportDialog.selectFile')}
              </Button>
            </label>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {fileName ||
                t('guidanceTourHealth.medicineReportDialog.noSelectedFile')}
            </Typography>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error">
          {t('accountInfosPage.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('accountInfosPage.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
